import React, { useContext, useMemo, useState } from 'react';
import { IRiskMitigationAction, Contributor, EntityValidations, ISpecificEntityFormProps, RiskRiskMitigationAction, ICorporateRiskMitigationAction, CorporateRiskMitigationAction, SaveStatus } from '../../types';
import { DateService, LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrDatePicker } from '../cr/CrDatePicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityStatus } from '../../refData/EntityStatus';
import { CrCheckbox } from '../cr/CrCheckbox';
import { EntityForm } from '../EntityForm';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';
import { ReportingCycleService } from '../../services/ReportingCycleService';
import { DataContext } from '../DataContext';
import { CrEntityPicker } from '../cr/CrEntityPicker';
import { CrCommandBar } from '../cr/CrCommandBar';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import EvidenceList from '../risk/EV/EvidenceList';
import { OrbUserContext } from '../OrbUserContext';

export class RiskMitigationActionValidations extends EntityValidations {
    public RiskID: string = null;
    public NormalContributors: string = null;
    public BaselineDate: string = null;
    public ReviewCycle: string = null;
}

export interface IRiskMitigationActionPageFormProps extends ISpecificEntityFormProps {
    maxDependentRisks?: number;
}

export const RiskMitigationActionPageForm = (props: IRiskMitigationActionPageFormProps): React.ReactElement => {
    const { maxDependentRisks } = props;
    const { dataServices: { contributorService, corporateRiskMitigationActionService, corporateRiskRiskMitigationActionService }, lookupData, loadLookupData: { corporateRisks, directorates,
        entityStatuses, financialRisks, userDirectorates, userGroups, userProjects, users: { all: allUsers } } } = useContext(DataContext);

    const { userContext, userPermissions } = useContext(OrbUserContext);
    const currentUserId: number = userContext.UserId;
    const [evidence_ListFilterText, setEvidence_ListFilterText] = useState<string>('');
    useMemo(() => corporateRisks(), [corporateRisks]);
    useMemo(() => directorates(), [directorates]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => financialRisks(), [financialRisks]);
    useMemo(() => userDirectorates(), [userDirectorates]);
    useMemo(() => userGroups(), [userGroups]);
    useMemo(() => userProjects(), [userProjects]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (rma: IRiskMitigationAction): Promise<RiskMitigationActionValidations> => {
        const errors = new RiskMitigationActionValidations();

        if (rma.Title === null || rma.Title === '') {
            errors.Title = 'Risk mitigation action name is required';
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (rma.RiskID === null) {
            errors.RiskID = 'Risk is required';
            errors.Valid = false;
        }
        else {
            errors.RiskID = null;
        }

        if (rma.Contributors.some(ct => ct.ContributorUserID === rma.OwnerUserID)) {
            errors.NormalContributors = 'A user cannot be both the lead and a contributor';
            errors.Valid = false;
        }
        else {
            errors.NormalContributors = null;
        }

        if (rma.BaselineDate == null && !rma.ActionIsOngoing) {
            errors.BaselineDate = `Please estimate when this action will be delivered`;
            errors.Valid = false;
        } else {
            errors.BaselineDate = null;
        }

        if (rma.ActionIsOngoing && !ReportingCycleService.cycleIsValid({ frequency: rma.OngoingActionReviewFrequency, dueDay: rma.OngoingActionReviewDueDay, startDate: rma.OngoingActionReviewStartDate })) {
            errors.ReviewCycle = 'Please select all values for the review cycle';
            errors.Valid = false;
        }
        else {
            errors.ReviewCycle = null;
        }

        return Promise.resolve(errors);
    };
    const handleEvidence_ChangeFilterText = (value: string): void => {
        setEvidence_ListFilterText(value);
    }
    return (
        <div className={`${styles.cr} ${styles.updatesContainer}`} style={{ width: '100%' }}>
            <EntityForm<ICorporateRiskMitigationAction, RiskMitigationActionValidations>
                {...props}
                mode='page'
                entityName="Risk mitigating action"
                renderFormFields={({ changeCheckbox, changeCycle, changeDatePicker, changeDropdown, changeEntityPicker, changeMultiUserPicker, changeTextField, changeUserPicker }, formState, saveEntity) => {
                    const { FormData: action, ValidationErrors: errors, FormIsDirty, FormSaveStatus } = formState;
                    const risk = lookupData?.CorporateRisks.find(r => r.ID === action.RiskID);
                    const corpRisks = lookupData?.CorporateRisks.filter(r => r.IsProjectRisk !== true);

                    let commandBarItems: ICommandBarItemProps[] = [];

                    if (FormSaveStatus === SaveStatus.Pending) {
                        commandBarItems.push({
                            key: 'Save',
                            text: 'Saving...',
                            iconProps: { iconName: 'Sync' },
                            disabled: true,
                        });
                    } else if (!FormIsDirty) {
                        commandBarItems.push({
                            key: 'Close',
                            text: 'Close',
                            iconProps: { iconName: 'Cancel' },
                            onClick: (ev, item) => {
                                props.onCancelled();
                            },
                        });
                    } else {
                        commandBarItems.push(
                            {
                                key: 'Save',
                                text: 'Save',
                                iconProps: { iconName: 'Save' },
                                onClick: (ev, item) => {
                                    saveEntity();
                                },
                            },
                            {
                                key: 'Cancel',
                                text: 'Cancel',
                                iconProps: { iconName: 'Cancel' },
                                onClick: (ev, item) => {
                                    props.onCancelled();
                                },
                            }
                        );
                    }

                    const mappedUsers = LookupService.riskMitigationActionUsers(lookupData, action);
                    const suggestions = risk != null ? {
                        initialSuggestionsHeaderText:
                            risk?.IsProjectRisk && risk?.ProjectID ? `Project users` :
                                risk?.DirectorateID ? `Directorate and group users` : null,
                        initialSuggestions: mappedUsers,
                        noResultsFoundText:
                            mappedUsers.length === 0 ?
                                risk?.IsProjectRisk && risk?.ProjectID ? `No users are mapped to this risk mitigating action's project` :
                                    risk?.DirectorateID ? `No users are mapped to this risk mitigating action's directorate or group` : null : null
                    } : {};
                    return (
                        <div>
                            <CrCommandBar items={commandBarItems} />
                            {/* Basic Details Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '10px' /*marginTop: '30px'*/ }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Basic Details</div>
                                    </div>
                                </div>
                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Name</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    required={true}
                                                    value={action.Title}
                                                    onChange={(_, v) => changeTextField(v, 'Title')}
                                                    maxLength={255}
                                                    errorMessage={errors.Title}
                                                    disabled={action?.Risk?.CustomFields?.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2nd row */}
                                    {!(props.showEntityDescription1 === true) && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Lead risk</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    required={true}
                                                    options={LookupService.entitiesToSelectableOptions(corpRisks, { optionText: r => `${r.RiskCode} - ${r.Title}` })}
                                                    selectedKey={action.RiskID}
                                                    onChange={(_, option, index) => changeDropdown(option, 'RiskID', index)}
                                                    errorMessage={errors.RiskID}
                                                    disabled={action?.Risk?.CustomFields?.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                />
                                            </div>
                                        </div>
                                    </div>}

                                    {/* 3rd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>{`Dependent risk${(maxDependentRisks || 1) > 1 ? 's' : ''}`}</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrEntityPicker
                                                    itemLimit={maxDependentRisks || 1}
                                                    entities={corpRisks.map(r => ({ key: r.ID, ID: r.ID, Title: `${r.RiskCode} - ${r.Title}` }))}
                                                    selectedEntities={action.CorporateRiskRiskMitigationActions?.map(rrma => rrma.RiskID)}
                                                    onChange={risks => changeEntityPicker(risks, 'CorporateRiskRiskMitigationActions', new RiskRiskMitigationAction(), 'RiskID')}
                                                    errorMessage={errors.RiskID}
                                                    disabled={action?.Risk?.CustomFields?.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 4th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Description</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrTextField
                                                    value={action.Description}
                                                    onChange={(_, v) => changeTextField(v, 'Description')}
                                                    maxLength={750}
                                                    multiline
                                                    rows={6}
                                                    disabled={action?.Risk?.CustomFields?.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 5th row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Is the action ongoing?</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrCheckbox
                                                    label="Yes"
                                                    checked={action.ActionIsOngoing}
                                                    disabled={action?.Risk?.CustomFields?.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                    onChange={(_, isChecked) =>
                                                        isChecked ?
                                                            changeCheckbox(isChecked, 'ActionIsOngoing', () => changeDatePicker(null, 'BaselineDate'))
                                                            : changeCheckbox(isChecked, 'ActionIsOngoing')
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 6th row */}
                                    {!action.ActionIsOngoing && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Baseline delivery date</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDatePicker
                                                    required={true}
                                                    value={DateService.removeTimezoneOffset(action.BaselineDate)}
                                                    onSelectDate={d => changeDatePicker(d, 'BaselineDate')}
                                                    errorMessage={errors.BaselineDate}
                                                    disabled={action?.Risk?.CustomFields?.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                                                />
                                            </div>
                                        </div>
                                    </div>}

                                    {/* 7th row */}
                                    {action.ActionIsOngoing && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Review cycle</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrReportingCyclePicker
                                                    required={true}
                                                    cycle={{ frequency: action.OngoingActionReviewFrequency, dueDay: action.OngoingActionReviewDueDay, startDate: action.OngoingActionReviewStartDate }}
                                                    onChange={cycle => changeCycle(cycle, { frequency: 'OngoingActionReviewFrequency', dueDay: 'OngoingActionReviewDueDay', startDate: 'OngoingActionReviewStartDate' })}
                                                    errorMessage={errors.ReviewCycle}
                                                />
                                            </div>
                                        </div>
                                    </div>}

                                    {/* 8th row */}
                                    {formState.FormDataBeforeChanges.EntityStatusID === EntityStatus.Closed && <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Status</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrDropdown
                                                    options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                                    selectedKey={action.EntityStatusID}
                                                    onChange={(_, o) => changeDropdown(o, 'EntityStatusID')}
                                                />
                                            </div>
                                        </div>
                                    </div>}
                                </div>
                            </div>

                            {/* Ownership and Roles Section */}
                            <div>
                                <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                    <div style={{ display: 'flex' }}>
                                        <div className={styles.sectionATitle}>Ownership and Roles</div>
                                    </div>
                                </div>
                                <div style={{ paddingRight: '10px', paddingLeft: '10px', paddingTop: '20px', paddingBottom: '0px', backgroundColor: 'rgb(245,245,245)', border: '1px solid rgb(230,230,230)', }}>
                                    {/* 1st row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Mitigating action owner</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrUserPicker
                                                    disabled={(!action.RiskID) || (action?.Risk?.CustomFields?.filter(x => x.CustomFieldDefnID === 1000).length > 0)}
                                                    users={mappedUsers}
                                                    selectedUsers={action.OwnerUserID && [action.OwnerUserID]}
                                                    onChange={u => changeUserPicker(u, 'OwnerUserID')}
                                                    {...suggestions}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* 2nd row */}
                                    <div className={styles.formField}>
                                        <div style={{ display: 'flex' }}>
                                            <div style={{ width: '100%', fontWeight: 'bold' }}>
                                                <div className={styles.flexContainerSectionQuestion}>
                                                    <div className={styles.sectionQuestionCol1}><span>Contributors</span></div>
                                                    <div className={styles.sectionQuestionCol2}>

                                                        {/* inner flex to line 2 images */}
                                                        <div style={{ display: 'flex' }}>
                                                            {/* <div><img src={reqWorkPurposeValidationImg} /></div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', marginTop: '5px' }}>
                                            <div style={{ width: '100%' }}>
                                                <CrUserPicker
                                                    disabled={!action.RiskID}
                                                    users={mappedUsers}
                                                    itemLimit={6}
                                                    selectedUsers={action.Contributors?.map(c => c.ContributorUserID)}
                                                    onChange={v => changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                                                    errorMessage={errors.NormalContributors}
                                                    {...suggestions}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    );
                }}
                loadEntity={id => corporateRiskMitigationActionService.read(id, true, true)}
                loadNewEntity={() => new CorporateRiskMitigationAction()}
                loadEntityValidations={() => new RiskMitigationActionValidations()}
                onValidateEntity={validateEntity}
                onCreate={rma => corporateRiskMitigationActionService.create(rma)}
                onUpdate={rma => corporateRiskMitigationActionService.update(rma.ID, rma)}
                parentEntities={corporateRiskMitigationActionService.parentEntities}
                childEntities={[
                    {
                        ObjectParentProperty: 'CorporateRiskRiskMitigationActions',
                        ParentIdProperty: 'RiskMitigationActionID',
                        ChildIdProperty: 'RiskID',
                        ChildService: corporateRiskRiskMitigationActionService
                    },
                    {
                        ObjectParentProperty: 'Contributors',
                        ParentIdProperty: 'RiskMitigationActionID',
                        ChildIdProperty: 'ContributorUserID',
                        ChildService: contributorService
                    }
                ]}
            />

            {props.entityId > 0 &&
                <>
                    <div>
                        <div style={{ marginBottom: '20px', marginTop: '50px' }}>
                            <div style={{ display: 'flex' }}>
                                <div className={styles.sectionATitle}>Discussion, General Comments and File Attachments</div>
                            </div>
                        </div>
                    </div>
                    <div>

                        <div style={{ minHeight: '120px' }}>
                            <EvidenceList
                                evidenceFor='RiskMitigationAction'
                                issueActionID={null}
                                issueID={null}
                                incidentActionID={null}
                                incidentID={null}
                                riskID={null}
                                riskMitigationActionID={props.entityId}
                                currentUserId={currentUserId}
                                superUserPermission={userPermissions.UserIsSystemAdmin()}
                                isViewOnly={false}
                                filterText={evidence_ListFilterText}
                                onChangeFilterText={handleEvidence_ChangeFilterText}
                                evChangesCounter={1}
                                {...props}
                                errorHandling={props.errorHandling}
                            />
                        </div>

                    </div>
                </>
            }
        </div>
    );
};
